import React, { useEffect, useState } from "react";
import { Tabs, Tab } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import TwoFactorSetup from "./TwoFactorSetup";
import PasswordStrengthMeter from "../Auth/components/PasswordStrengthMeter";
import { actions } from "../Auth/_redux/authRedux";
import {
  updateProfile,
  changePassword as changePasswordApi,
  login,
} from "../Auth/_redux/authCrud";

const STRONG_PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;

function PasswordField({ value, onChange, required }) {
  const [visible, setVisible] = useState(false);

  return (
    <div style={{ position: "relative" }}>
      <input
        type={visible ? "text" : "password"}
        className="form-control"
        style={{ paddingRight: "2.25rem" }}
        value={value}
        onChange={onChange}
        required={required}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        style={{
          position: "absolute",
          right: "0.5rem",
          top: "50%",
          transform: "translateY(-50%)",
          background: "transparent",
          border: "none",
          padding: 0,
          lineHeight: 1,
        }}
        aria-label={visible ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
      >
        <i className={visible ? "fas fa-eye-slash" : "fas fa-eye"}></i>
      </button>
    </div>
  );
}

export default function ProfilePage() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    new URLSearchParams(location.search).get("tab") === "security"
      ? "security"
      : "profile"
  );
  const user = useSelector(({ auth }) => auth.user);
  const [twoFactorEnabled, setTwoFactorEnabledState] = useState(
    Boolean(user && user.has_two_factor_enabled)
  );
  const [name, setName] = useState((user && user.name) || "");
  const [email, setEmail] = useState((user && user.email) || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("tab") === "security") {
      setActiveTab("security");
    }
  }, [location.search]);

  const setTwoFactorEnabled = (enabled) => {
    setTwoFactorEnabledState(enabled);
    dispatch(actions.setTwoFactorEnabled(enabled));
  };

  const submitProfile = (e) => {
    e.preventDefault();
    setSavingProfile(true);
    updateProfile(name, email)
      .then(() => {
        dispatch(actions.setProfileInfo(name, email));
        toast.success("Profil berhasil diperbarui.");
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.error || "Gagal memperbarui profil."
        );
      })
      .finally(() => setSavingProfile(false));
  };

  const submitPassword = (e) => {
    e.preventDefault();

    if (!STRONG_PASSWORD_PATTERN.test(newPassword)) {
      toast.error(
        "Password minimal 12 karakter, kombinasi huruf besar, huruf kecil, angka, dan simbol."
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Konfirmasi password tidak cocok dengan password baru.");
      return;
    }

    setSavingPassword(true);
    changePasswordApi(user.nik, oldPassword, newPassword, confirmPassword)
      .then(() => login(user.nik, newPassword))
      .then(({ data }) => {
        dispatch(actions.changePassword(data));
        toast.success("Password berhasil diganti.");
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
      })
      .catch(() => toast.error("Gagal mengganti password. Cek password lama Anda."))
      .finally(() => setSavingPassword(false));
  };

  return (
    <div className="card card-custom">
      <div className="card-header">
        <h3 className="card-title">Profil Saya</h3>
      </div>
      <div className="card-body">
        <Tabs
          activeKey={activeTab}
          onSelect={(key) => setActiveTab(key)}
          id="profile-settings-tabs"
          className="mb-6"
        >
          <Tab eventKey="profile" title="Profile">
            <form onSubmit={submitProfile} className="mt-6">
              <div className="form-group">
                <label>Nama</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>NIK</label>
                <input
                  type="text"
                  className="form-control"
                  value={(user && user.nik) || ""}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={savingProfile}
              >
                Simpan Profil
              </button>
            </form>
          </Tab>

          <Tab eventKey="security" title="Security">
            <div className="mt-6">
              <h5>Ganti Password</h5>
              <form onSubmit={submitPassword} className="mb-6">
                <div className="form-group">
                  <label>Password Lama</label>
                  <PasswordField
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password Baru</label>
                  <PasswordField
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <PasswordStrengthMeter value={newPassword} light />
                </div>
                <div className="form-group">
                  <label>Konfirmasi Password Baru</label>
                  <PasswordField
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingPassword}
                >
                  Simpan Password
                </button>
              </form>

              <hr />

              <h5>Verifikasi Dua Langkah (2FA)</h5>
              <TwoFactorSetup enabled={twoFactorEnabled} onChange={setTwoFactorEnabled} />
            </div>
          </Tab>
        </Tabs>
      </div>
    </div>
  );
}
